import React, { FC } from 'react';
import TextLink, { TextLinkTargetType } from '../../../ui/TextLink/TextLink';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarClickTypes,
  NarvarEventComponents,
  NarvarClickSubTypes,
} from '../../../../hooks/Analytics';
import { useTranslations } from '../../../../hooks/useTranslations';
import { LayoutTree } from '../../DynamicLayout';
import useOrderTracking from '../../../../hooks/useOrderTracking';
import { useTrackQualityCarrierLogo } from './hooks/useTrackQualityCarrierLogo';
import { useTheme } from '../../../../hooks/useTheme';

const { FeatureLayout } = LayoutTree;

export interface CarrierTrackingLinkProps {
  featureName?: string;
  layout?: LayoutTree.FeatureLayout;
}

const trackingNumberId = 'tracking-number';

const CarrierTrackingLink: FC<CarrierTrackingLinkProps> = ({
  featureName,
  layout = FeatureLayout.PORTRAIT,
}) => {
  const {
    bodytextDefaultNone: bodyTextStyle,
    linksTrackingDefault: linkStyle,
    linksTrackingHover: linkHoverStyle,
  } = useTheme();

  const { shippingActivityTrackingNumberText } = useTranslations();

  const { getCurrentShipment } = useOrderTracking();
  const currentShipment = getCurrentShipment();

  const logEvent = useAnalyticsPost();

  const { ref: imgRef, clientCarrierLogo } =
    useTrackQualityCarrierLogo(currentShipment);

  const {
    trackingNumber = '',
    trackingUrl = '',
    carrier,
    carrierLogoUrl,
    history,
  } = currentShipment ?? {};

  if (!currentShipment) return null;
  if (!trackingNumber) return null;

  return (
    <p style={{ display: 'flex', alignItems: 'center', marginBottom: 0 }}>
      {(!history || history.length === 0) && (
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            height: '2.5rem',
            paddingRight: '0.5rem',
          }}
        >
          <img
            ref={imgRef}
            // `carrierLogoUrl` is nullable on the orderTracking query because of
            // scenarios like preship and bopis. However, it will be populated with
            // at least a the generic logo url in every other case.
            src={clientCarrierLogo ?? carrierLogoUrl ?? ''}
            alt={`${carrier ?? ''}`}
            style={{
              maxHeight: '2.5rem',
            }}
          />
        </span>
      )}
      <span
        data-test="shippingHistoryTrackingNumberText"
        style={{
          display: 'inline-block',
          verticalAlign: 'middle',
          wordBreak: 'break-all',
          ...bodyTextStyle,
        }}
      >
        {shippingActivityTrackingNumberText}
        <br />
        {trackingUrl ? (
          <>
            <TextLink
              target={TextLinkTargetType.BLANK}
              linkStyle={{ sDefault: linkStyle, sHover: linkHoverStyle }}
              href={trackingUrl}
              onClick={(): void =>
                logEvent({
                  clickCategory: NarvarClickSubTypes.EXTERNAL_TRACKING_LINK,
                  clickLinkDestinationUrl: trackingUrl,
                  clickSubType: NarvarClickSubTypes.EXTERNAL_TRACKING_LINK,
                  clickType: NarvarClickTypes.OUTGOING,
                  eventCarrierMoniker: carrier,
                  eventComponent:
                    NarvarEventComponents.NTH_BUNDLE_SHIPPING_ACTIVITY,
                  eventFeature:
                    NarvarEventComponents.NTH_BUNDLE_SHIPPING_ACTIVITY,
                  eventTrackingNumber: trackingNumber,
                  eventType: NarvarEventTypes.CLICK,
                  feature: featureName,
                  pageComponent:
                    NarvarEventComponents.NTH_BUNDLE_SHIPPING_ACTIVITY,
                  trackLayout: layout,
                })
              }
            >
              {trackingNumber}
            </TextLink>
          </>
        ) : (
          <span
            style={{
              ...bodyTextStyle,
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
            }}
          >
            <span id={trackingNumberId}>{trackingNumber}</span>
          </span>
        )}
      </span>
    </p>
  );
};

export default CarrierTrackingLink;
